/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';

import { Badge, Button, FlexCol, FlexRow, Icon, Tooltip } from 'sarsaparilla';

import { storeCampsiteClicked } from 'ui-camping/src/camping/utils/campground';
import {
    CAMPSITE_AVAILABLE,
    LABEL_FIRST_COME_FIRST_SERVED,
    LABEL_AVAILABLE,
    LABEL_UNAVAILABLE,
    LABEL_PARTIALLY_AVAILABLE,
    LABEL_NOT_YET_RELEASED,
} from 'ui-camping/src/shared/constants/availability';
import { ACCESSIBLE_CAMPSITE_TOOLTIP } from 'ui-camping/src/shared/constants/campsite';
import {
    formatUTCDateFnsDate,
    searchDateToUTCString,
} from 'ui-camping/src/shared/utils/dateUtils';
import { CAMPSITE_LIST } from 'ui-camping/src/shared/constants/campground';

import { createComponent, handleClosePopUp } from './common';

export default function campingMapPopup(
    {
        imageUrl,
        title,
        location,
        attributes,
        targetUri,
        featureId,
        entityId,
        isItinerary,
        onClickFunction,
        accessible,
        campsite_type,
        isInternal,
        startDate,
        endDate,
        isSiteView,
        currentSiteViewId,
        siteAvailability,
        gridStartDate,
        gridEndDate,
        calendarAvailabilityStatus,
        fullGridStartDate,
        fullGridEndDate,
        selectedTab,
        hasSelectedGridDates,
        calendarStartDate,
        calendarEndDate,
        isNSS,
    },
    deselectFeature
) {
    const isMobile = window.innerWidth < 768;
    const isListTab = selectedTab === CAMPSITE_LIST;
    const isSelectedSiteView = currentSiteViewId === featureId?.split('_')?.[1];
    let campgroundId = null;

    if (featureId) {
        [campgroundId] = featureId.split('_');
    }

    const isAccessible =
        typeof accessible === 'string' ? JSON.parse(accessible) : accessible;
    let imgUrl = imageUrl === 'null' ? null : imageUrl;

    if (isSiteView && !!siteAvailability && !imgUrl) {
        imgUrl = `${process.env.CDN}/img/global/unavailable/image-unavailable.jpg`;
    }

    const siteAttributes =
        attributes && typeof attributes === 'string'
            ? JSON.parse(attributes)
            : attributes;
    const targetUrl = targetUri === 'null' ? null : targetUri;

    const formatDate = (date) => {
        return formatUTCDateFnsDate(date, 'MM/dd/yy');
    };

    const getDateLabel = (start, end) => {
        if (!start || !end) return '';
        return `${formatDate(start)} to ${formatDate(end)}`;
    };

    // availability based on search dates
    const calendarStatuses = {
        isAvailable: calendarAvailabilityStatus === LABEL_AVAILABLE,
        isUnavailable: calendarAvailabilityStatus === LABEL_UNAVAILABLE,
        isNotReservable: calendarAvailabilityStatus === LABEL_FIRST_COME_FIRST_SERVED,
        isPartiallyAvailable: calendarAvailabilityStatus === LABEL_PARTIALLY_AVAILABLE,
        isNotYetReleased: calendarAvailabilityStatus === LABEL_NOT_YET_RELEASED,
    };

    const hasCalendarDate = calendarStartDate && calendarEndDate;
    const calendarDatesText = hasCalendarDate
        ? getDateLabel(
              searchDateToUTCString(calendarStartDate),
              searchDateToUTCString(calendarEndDate)
          )
        : '';
    const gridDatesText = getDateLabel(gridStartDate, gridEndDate);
    const fullGridDatesText = getDateLabel(fullGridStartDate, fullGridEndDate);

    const hasDates = isListTab
        ? hasCalendarDate
        : hasSelectedGridDates || hasCalendarDate;

    const dateTextContent = isListTab
        ? calendarDatesText
        : gridDatesText || calendarDatesText || fullGridDatesText;

    const isAvailableSite = siteAvailability === CAMPSITE_AVAILABLE;
    const hasAvailability = siteAvailability === 'Available';
    const titleClassName = imgUrl ? '' : ' popup-title-no-image';
    const siteAvailabilityClassName = `map-popup-${hasAvailability ? '' : 'un'}availability-info`;
    const availability = (
        <div className={siteAvailabilityClassName}>{siteAvailability}</div>
    );

    const isAvailable =
        (startDate && endDate) ||
        (gridStartDate && gridEndDate) ||
        calendarStatuses.isAvailable;

    const getAvailabilitySection = () => {
        const useCalendarStatus = isListTab || !hasSelectedGridDates;

        const showAvailable = useCalendarStatus
            ? calendarStatuses.isAvailable
            : calendarStatuses.isAvailable || hasSelectedGridDates;
        const showUnavailable = useCalendarStatus
            ? calendarStatuses.isUnavailable
            : calendarStatuses.isUnavailable && !hasSelectedGridDates;
        const showPartiallyAvailable = useCalendarStatus
            ? calendarStatuses.isPartiallyAvailable
            : calendarStatuses.isPartiallyAvailable && !hasSelectedGridDates;
        const showNotReservable = calendarStatuses.isNotReservable && useCalendarStatus;
        const showNotYetReleased = calendarStatuses.isNotYetReleased;

        return (
            <div className="popup-availability-container">
                <FlexCol>
                    <FlexRow>
                        {hasDates && showAvailable && (
                            <span className="availability-grid-status-title status-available-grid">
                                Available
                            </span>
                        )}
                        {hasDates && showPartiallyAvailable && (
                            <span className="availability-grid-status-title status-partially-available-grid">
                                Partially available
                            </span>
                        )}
                        {hasDates && showNotYetReleased && (
                            <span className="availability-grid-status-title status-not-yet-released-list">
                                Not Yet Released
                            </span>
                        )}
                        {hasDates && showUnavailable && (
                            <span className="availability-grid-status-title status-unavailable-grid">
                                Unavailable
                            </span>
                        )}
                        {showNotReservable && (
                            <span className="availability-grid-status-title status-not-reservable-grid">
                                First-come, first-served
                            </span>
                        )}
                    </FlexRow>
                    {!showNotReservable && (
                        <FlexRow>
                            {hasDates &&
                                (showAvailable || showUnavailable) &&
                                !showPartiallyAvailable && <span>{dateTextContent}</span>}
                            {hasDates &&
                                showPartiallyAvailable &&
                                !showNotYetReleased && (
                                    <span>Check grid for available dates</span>
                                )}
                            {!hasDates && !showNotYetReleased && (
                                <span className="status-partially-available-list">
                                    Enter dates for availability
                                </span>
                            )}
                        </FlexRow>
                    )}
                    {showNotReservable && (
                        <FlexRow>
                            <span className="status-not-reservable-list">
                                This site is not available to reserve in advance online or
                                by phone. You must physically arrive at the facility to
                                see if the site is available. The site may be occupied
                                when you arrive.
                            </span>
                        </FlexRow>
                    )}
                </FlexCol>
            </div>
        );
    };

    const onClosePopup = () => {
        if (deselectFeature) deselectFeature();
        handleClosePopUp();
    };

    const providedStartDate =
        startDate ||
        gridStartDate ||
        (calendarStartDate ? searchDateToUTCString(calendarStartDate) : null);
    const providedEndDate =
        endDate ||
        gridEndDate ||
        (calendarEndDate ? searchDateToUTCString(calendarEndDate) : null);

    const popup = (
        <div className="map-popup">
            <div className="map-popup-message">
                <Button
                    className="close-pop-up-btn"
                    appearance="subtle"
                    size="sm"
                    shouldFitContainer={false}
                    onClick={onClosePopup}
                    iconBeforeElement={<Icon iconName="close" />}
                    screenReaderTextBefore="Close the modal"
                    gaTrackingId="681762123001"
                />

                {imgUrl ? (
                    <div
                        aria-hidden="true"
                        className="map-popup-preview-image"
                        style={{ backgroundImage: `url("${imgUrl}")` }}
                    >
                        {isSiteView && !!siteAvailability && availability}
                    </div>
                ) : null}

                <div className={`map-popup-title${titleClassName}`}>{title}</div>

                {location ? <p className="map-popup-text">{location}</p> : null}

                {isAccessible || !!siteAttributes?.length ? (
                    <ul>
                        {isAccessible && (
                            <li className="accessible-icon-list">
                                <Tooltip content={ACCESSIBLE_CAMPSITE_TOOLTIP}>
                                    <Icon
                                        iconName="accessible"
                                        size="sm"
                                        className="accessible-icon"
                                    />
                                </Tooltip>
                            </li>
                        )}
                        {isInternal && campsite_type ? <li>{campsite_type}</li> : null}
                        {siteAttributes
                            ? siteAttributes.map(({ key, value }) => {
                                  const hasAvailabilityAttribute = key === 'availability';
                                  if (hasAvailabilityAttribute) return null;
                                  return <li key={key}>{value}</li>;
                              })
                            : null}
                    </ul>
                ) : null}

                {(isAccessible || !!siteAttributes?.length) &&
                    !isSiteView &&
                    !isNSS &&
                    !isInternal &&
                    getAvailabilitySection()}

                {((!isItinerary && targetUrl) || isAvailable) && !isInternal ? (
                    <div className="map-popup-button-wrap">
                        <div className="map-popup-buttons">
                            {!isItinerary &&
                                !(isSiteView && isSelectedSiteView) &&
                                targetUrl && (
                                    <div>
                                        <Button
                                            href={targetUrl}
                                            appearance="link"
                                            onClick={() =>
                                                storeCampsiteClicked(
                                                    campgroundId,
                                                    entityId
                                                )
                                            }
                                            rel="noopener noreferrer"
                                            size="md"
                                            gaTrackingId="317756999568"
                                            gaTrackingValue={title}
                                        >
                                            View Details
                                        </Button>
                                        {featureId === '250860_583320' && (
                                            <Badge appearance="info">
                                                Portage required
                                            </Badge>
                                        )}
                                    </div>
                                )}

                            {hasDates &&
                                !calendarStatuses.isNotReservable &&
                                (calendarStatuses.isAvailable || isAvailableSite) && (
                                    <div>
                                        <Button
                                            id={`book-now-button-${entityId}`}
                                            className={`list-map-book-now-button-tracker ${
                                                isMobile ? 'mobile' : 'desktop'
                                            }`}
                                            onClick={() =>
                                                onClickFunction({
                                                    [entityId]: {
                                                        startDate: providedStartDate,
                                                        endDate: providedEndDate,
                                                        dateRangeMap: isItinerary
                                                            ? {
                                                                  [providedStartDate]:
                                                                      providedEndDate,
                                                              }
                                                            : undefined,
                                                    },
                                                })
                                            }
                                            appearance="primary"
                                            size="xs"
                                            rel="noopener noreferrer"
                                            gaTrackingId="253459600897"
                                            gaTrackingValue={title}
                                        >
                                            Add to Cart
                                        </Button>
                                    </div>
                                )}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );

    return createComponent(popup);
}
